import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as JiraIcon } from '@elementary/common-ui/assets/jira-full.svg';
import { ReactComponent as LinearIcon } from '@elementary/common-ui/assets/linear-full.svg';
import { ReactComponent as ServiceNowIcon } from '@elementary/common-ui/assets/servicenow-full.svg';
import { Button, Flex } from '@elementary/common-ui/components';
import {
  ServiceNowConfigApi,
  TicketingForm,
  TicketingIntegration,
  TicketingTool,
} from 'shared/types';
import { IntegrationError } from 'settings/types';
import { space } from '@elementary/common-ui/style/utils';
import IntegrationDialog from '../../../shared/Integration/IntegrationDialog';
import ServiceNowAppConfigContent, {
  ServiceNowAppConfig,
  ServiceNowDialogConfig,
} from './ServiceNowAppConfigContent';
import ToolDialogContent, { ToolDialogConfig } from './ToolDialogContent';

const ProviderButton = styled(Button)`
  display: flex;
  height: 120px;
  padding: ${space('xxl')};
  justify-content: center;
  align-items: center;
  gap: ${space('xxl')};
  flex: 1 0 0;
`;

type Props = {
  open: boolean;
  onClose: () => void;
  data?: TicketingIntegration;
  onSubmit: (data: TicketingForm) => void;
  loading?: boolean;
  error?: IntegrationError;
  envId: string;
  servicenowConfig?: ServiceNowConfigApi;
};

enum OuterStep {
  toolSelection,
  appConfig,
  tool,
}

const TicketingToolDialog = ({
  open,
  onClose,
  data,
  onSubmit,
  loading,
  error,
  envId,
  servicenowConfig,
}: Props) => {
  const [outerStep, setOuterStep] = useState(OuterStep.toolSelection);
  const [selectedTool, setSelectedTool] = useState<TicketingTool>();
  const [appConfig, setAppConfig] = useState<ServiceNowAppConfig>();
  const [snDialogConfig, setSnDialogConfig] = useState<ServiceNowDialogConfig>({
    isValid: false,
    onSubmit: () => {},
    reset: () => {},
  });
  const [toolDialogConfig, setToolDialogConfig] = useState<ToolDialogConfig>({
    title: '',
    onSubmit: () => {},
    isValid: false,
    reset: () => {},
  });

  useEffect(() => {
    if (!open) {
      setOuterStep(OuterStep.toolSelection);
      setSelectedTool(undefined);
    }
  }, [open]);

  useEffect(() => {
    if (data?.enabled && data?.type) {
      setSelectedTool(data.type);
      setOuterStep(OuterStep.tool);
    }
  }, [data?.enabled, data?.type]);

  const handleToolSelection = useCallback(
    (tool: TicketingTool) => {
      setSelectedTool(tool);
      if (tool === 'servicenow') {
        if (servicenowConfig) {
          setAppConfig({
            configKey: servicenowConfig.config_key,
            subdomain: servicenowConfig.subdomain,
          });
          setOuterStep(OuterStep.tool);
        } else {
          setOuterStep(OuterStep.appConfig);
        }
      } else {
        setOuterStep(OuterStep.tool);
      }
    },
    [servicenowConfig],
  );

  const handleSnCreated = useCallback((config: ServiceNowAppConfig) => {
    setAppConfig(config);
    setOuterStep(OuterStep.tool);
  }, []);

  const handleSnDialogProps = useCallback((config: ServiceNowDialogConfig) => {
    setSnDialogConfig(config);
  }, []);

  const handleToolDialogProps = useCallback((config: ToolDialogConfig) => {
    setToolDialogConfig(config);
  }, []);

  const isToolSelectionStep = outerStep === OuterStep.toolSelection;
  const isAppConfigStep = outerStep === OuterStep.appConfig;

  const title = isToolSelectionStep
    ? 'Connect your ticketing tool'
    : isAppConfigStep
    ? 'Connect your ServiceNow app'
    : toolDialogConfig.title;

  const isValid = isToolSelectionStep
    ? false
    : isAppConfigStep
    ? snDialogConfig.isValid
    : toolDialogConfig.isValid;

  const dialogOnSubmit = isToolSelectionStep
    ? undefined
    : isAppConfigStep
    ? snDialogConfig.onSubmit
    : toolDialogConfig.onSubmit;

  const saveButtonText = isToolSelectionStep
    ? undefined
    : isAppConfigStep
    ? 'Connect'
    : toolDialogConfig.saveButtonText;

  const dialogReset = isToolSelectionStep
    ? undefined
    : isAppConfigStep
    ? snDialogConfig.reset
    : toolDialogConfig.reset;

  const dialogError = isToolSelectionStep
    ? undefined
    : isAppConfigStep
    ? snDialogConfig.error
    : toolDialogConfig.error;

  const dialogLoading = isToolSelectionStep
    ? false
    : isAppConfigStep
    ? snDialogConfig.loading
    : toolDialogConfig.loading;

  return (
    <IntegrationDialog
      open={open}
      onClose={onClose}
      title={title}
      description={
        isAppConfigStep
          ? 'Give Elementary access to your ServiceNow instance by creating a new app.'
          : undefined
      }
      docsLink={
        isAppConfigStep
          ? 'https://docs.elementary-data.com/cloud/integrations/alerts/servicenow'
          : undefined
      }
      hideSaveButton={isToolSelectionStep}
      isValid={isValid}
      onSubmit={dialogOnSubmit}
      saveButtonText={saveButtonText}
      reset={dialogReset}
      error={dialogError}
      loading={dialogLoading}
    >
      {isToolSelectionStep && (
        <Flex gap={3}>
          <ProviderButton
            aria-label="Linear"
            variant="outlined"
            onClick={() => handleToolSelection('linear')}
          >
            <LinearIcon />
          </ProviderButton>
          <ProviderButton
            aria-label="Jira"
            variant="outlined"
            onClick={() => handleToolSelection('jira')}
          >
            <JiraIcon />
          </ProviderButton>
          <ProviderButton
            aria-label="ServiceNow"
            variant="outlined"
            onClick={() => handleToolSelection('servicenow')}
          >
            <ServiceNowIcon />
          </ProviderButton>
        </Flex>
      )}

      {isAppConfigStep && (
        <ServiceNowAppConfigContent
          envId={envId}
          onCreate={handleSnCreated}
          onDialogProps={handleSnDialogProps}
        />
      )}

      {outerStep === OuterStep.tool && selectedTool && (
        <ToolDialogContent
          open={open}
          data={data}
          onSubmit={onSubmit}
          error={error}
          loading={loading}
          envId={envId}
          type={selectedTool}
          appConfig={appConfig}
          onDialogProps={handleToolDialogProps}
        />
      )}
    </IntegrationDialog>
  );
};

export default TicketingToolDialog;
